import React, { useId } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "./business-impact.module.scss";
import {
  formatNumber,
  formatPercentOneDecimal,
  formatSignedPercent,
} from "./business-impact-view";
import { RoiSample } from "./sample";

const K = "admin:companyDashboard.businessImpact.stress";

const BANDS = ["high", "moderate", "low"] as const;

const TrendDown = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path
      d="M2.5 5.5l5 5 3-3 7 7M12.5 14.5h5v-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowUpRight = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path
      d="M5.5 14.5l9-9M7.5 5.5h7v7"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StressLevelChanges = ({ sample }: { sample: RoiSample }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const locale = i18n.language;
  const { stress, stressChange } = sample;
  const id = useId();
  const highReduction = formatPercentOneDecimal(
    Math.abs(stressChange.high.percent),
    locale
  );
  const number = (value: number) => formatNumber(value, locale);
  const employees = (key: string, change: number) =>
    t(`${K}.tiles.${key}`, {
      count: Math.abs(change),
      formatted: number(Math.abs(change)),
    });

  return (
    <section className={styles["card"]}>
      <div>
        <h3 className={styles["section-heading"]}>{t(`${K}.title`)}</h3>
        <p className={styles["subheading"]}>{t(`${K}.subtitle`)}</p>
      </div>
      <p className={styles["callout"]}>
        <span className={styles["callout-icon"]}>
          <TrendDown />
        </span>
        {sample.version === "projected"
          ? t(`${K}.summaryProjected`, {
              percent: highReduction,
              months: sample.basis.months,
            })
          : t(`${K}.summary`, { percent: highReduction })}
      </p>

      {(["before", "after"] as const).map((when) => {
        const row = stress[when];
        return (
          <div
            key={when}
            className={styles["stack-row"]}
            role="group"
            aria-labelledby={`${id}-${when}`}
          >
            <div className={styles["stack-head"]}>
              <span id={`${id}-${when}`}>{t(`${K}.${when}`)}</span>
              <span className={styles["stack-total"]}>
                {t(`${K}.employees`, {
                  count: row.employees,
                  formatted: number(row.employees),
                })}
              </span>
            </div>
            <div className={styles["stack"]} aria-hidden="true">
              {BANDS.map((band) => (
                <span
                  key={band}
                  className={styles[`band-${band}`]}
                  style={{ width: `${(row[band] / row.employees) * 100}%` }}
                />
              ))}
            </div>
            <ul className={styles["stack-labels"]}>
              {BANDS.map((band) => (
                <li key={band}>
                  {t(`${K}.bandCount.${band}`, {
                    formatted: number(row[band]),
                  })}
                </li>
              ))}
            </ul>
          </div>
        );
      })}

      <ul className={styles["stress-legend"]}>
        {BANDS.map((band) => (
          <li key={band}>
            <span
              className={`${styles["dot"]} ${styles[`band-${band}`]}`}
              aria-hidden="true"
            />
            {t(`${K}.bands.${band}`)}
          </li>
        ))}
      </ul>

      <div className={styles["tiles"]}>
        <div
          className={styles["tile"]}
          role="group"
          aria-labelledby={`${id}-high`}
        >
          <div className={styles["tile-head"]}>
            <h4 id={`${id}-high`} className={styles["tile-title"]}>
              {t(`${K}.tiles.highTitle`)}
            </h4>
            <span className={styles["tile-icon-down"]}>
              <TrendDown />
            </span>
          </div>
          <p className={styles["tile-figure"]}>
            {formatSignedPercent(stressChange.high.percent, locale)}
          </p>
          <p className={styles["tile-note"]}>
            {employees("highEmployees", stressChange.high.employees)}
          </p>
        </div>
        <div
          className={styles["tile"]}
          role="group"
          aria-labelledby={`${id}-low`}
        >
          <div className={styles["tile-head"]}>
            <h4 id={`${id}-low`} className={styles["tile-title"]}>
              {t(`${K}.tiles.lowTitle`)}
            </h4>
            <span className={styles["tile-icon-up"]}>
              <ArrowUpRight />
            </span>
          </div>
          <p className={styles["tile-figure"]}>
            {formatSignedPercent(stressChange.low.percent, locale)}
          </p>
          <p className={styles["tile-note"]}>
            {employees("lowEmployees", stressChange.low.employees)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default StressLevelChanges;
